import { PosSessionsApiService } from '@adminServices';
import { PosSessionsFacade } from '@adminStore/pos-sessions';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HelperResponseDto } from '@klubovna/api-client';
import { TranslateModule } from '@ngx-translate/core';
import { SelectedHelper } from '@posModels';
import { SharedModule } from '@shared';
import { debounceTime, finalize, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-manage-helpers-dialog',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    TranslateModule,
    MatDialogModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './manage-helpers-dialog.component.html',
  styleUrl: './manage-helpers-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageHelpersDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<ManageHelpersDialogComponent>);
  private posSessionsFacade = inject(PosSessionsFacade);
  private posSessionsApi = inject(PosSessionsApiService);
  private destroyRef = inject(DestroyRef);

  currentSession = this.posSessionsFacade.currentSession;
  helpers = this.posSessionsFacade.helpers;
  helpersLoading = this.posSessionsFacade.helpersLoading;

  volunteerSearchControl = new FormControl('');
  volunteerSearchResults = signal<SelectedHelper[]>([]);
  searchLoading = signal(false);
  removingId = signal<string | null>(null);

  ngOnInit(): void {
    const sessionId = this.currentSession()?.id;
    if (sessionId) {
      this.posSessionsFacade.loadHelpers(sessionId);
    }

    this.setupVolunteerSearch();
  }

  private setupVolunteerSearch(): void {
    this.volunteerSearchControl.valueChanges
      .pipe(
        debounceTime(300),
        takeUntilDestroyed(this.destroyRef),
        switchMap((value) => {
          const search = typeof value === 'string' ? value.trim() : '';
          if (search.length < 2) {
            this.volunteerSearchResults.set([]);
            return of(null);
          }

          this.searchLoading.set(true);
          return this.posSessionsApi.searchAvailableHelpers({ page: 1, limit: 50, search }).pipe(
            finalize(() => this.searchLoading.set(false)),
          );
        }),
      )
      .subscribe({
        next: (helpers) => {
          if (!helpers) return;

          const currentHelperIds = this.helpers().map(h => h.id);
          const filtered = helpers
            .filter(h => !currentHelperIds.includes(h.id))
            .map(h => ({
              id: h.id,
              firstName: h.firstName,
              lastName: h.lastName,
            }));
          this.volunteerSearchResults.set(filtered);
        },
        error: () => {
          this.volunteerSearchResults.set([]);
        },
      });
  }

  displayVolunteer = (volunteer: SelectedHelper | string | null): string => {
    if (!volunteer) return '';
    if (typeof volunteer === 'string') return volunteer;
    return `${ volunteer.firstName } ${ volunteer.lastName }`;
  };

  onVolunteerSelected(volunteer: SelectedHelper): void {
    const sessionId = this.currentSession()?.id;
    if (!sessionId) return;

    this.posSessionsFacade.addHelper(sessionId, { volunteerId: volunteer.id });
    this.volunteerSearchControl.setValue('');
    this.volunteerSearchResults.set([]);
  }

  removeHelper(helper: HelperResponseDto): void {
    const sessionId = this.currentSession()?.id;
    if (!sessionId) return;

    this.removingId.set(helper.id);
    this.posSessionsFacade.removeHelper(sessionId, helper.id);
  }

  close(): void {
    this.dialogRef.close();
  }

  getHelperName(helper: HelperResponseDto): string {
    return `${ helper.firstName } ${ helper.lastName }`;
  }
}
